import { useEffect, useRef, useState } from "react";
import { useApiIsLoaded, useMapsLibrary } from "@vis.gl/react-google-maps";

interface AddressAutocompleteProps {
  onAddressSelect: (address: string, lat: number, lng: number) => void;
  /**
   * Fires whenever the user types or pastes text in the input, even if they
   * never pick a suggestion (i.e. they wrote a custom address). Use this to
   * keep a form field in sync with the raw text — coordinates won't be
   * available in that case, but at least the address won't be lost.
   */
  onAddressTextChange?: (text: string) => void;
  placeholder?: string;
  className?: string;
  defaultValue?: string;
  value?: string;
}

// Minimal shapes for the Places API (New) suggestion layer. The installed
// @types/google.maps may not cover AutocompleteSuggestion yet, so we type the
// bits we touch and read the classes off the runtime `places` library.
interface PlacePrediction {
  placeId: string;
  text: { text: string };
  toPlace: () => {
    fetchFields: (opts: { fields: string[] }) => Promise<unknown>;
    formattedAddress?: string | null;
    location?: { lat: () => number; lng: () => number } | null;
  };
}
interface Suggestion {
  placePrediction?: PlacePrediction | null;
}

const MIN_QUERY = 3;
const DEBOUNCE_MS = 250;

export default function AddressAutocomplete({
  onAddressSelect,
  onAddressTextChange,
  placeholder = "Buscar dirección...",
  className = "",
  defaultValue = "",
  value,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const apiIsLoaded = useApiIsLoaded();
  // The "places" library (New) exposes AutocompleteSuggestion +
  // AutocompleteSessionToken. We replaced the legacy `Autocomplete` widget
  // (not available to new GCP projects, and it spams the console) with this
  // programmatic Data API so we keep full control over the native <input>.
  const placesLibrary = useMapsLibrary("places") as unknown as {
    AutocompleteSuggestion?: {
      fetchAutocompleteSuggestions: (req: Record<string, unknown>) => Promise<{
        suggestions: Suggestion[];
      }>;
    };
    AutocompleteSessionToken?: new () => object;
  } | null;

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [open, setOpen] = useState(false);
  const sessionTokenRef = useRef<object | null>(null);

  // Keep the input text in sync when a value is controlled from the parent
  // (e.g. GeofenceForm's reverse-geocoder updates it on marker drag).
  useEffect(() => {
    if (value !== undefined && inputRef.current && inputRef.current.value !== value) {
      inputRef.current.value = value;
    }
  }, [value]);

  // Debounced suggestion fetch via Places API (New).
  useEffect(() => {
    if (!placesLibrary?.AutocompleteSuggestion) return;
    if (query.trim().length < MIN_QUERY) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        if (!sessionTokenRef.current && placesLibrary.AutocompleteSessionToken) {
          sessionTokenRef.current = new placesLibrary.AutocompleteSessionToken();
        }
        const { suggestions: results } =
          await placesLibrary.AutocompleteSuggestion!.fetchAutocompleteSuggestions({
            input: query,
            includedRegionCodes: ["mx"],
            ...(sessionTokenRef.current
              ? { sessionToken: sessionTokenRef.current }
              : {}),
          });
        if (cancelled) return;
        const preds = (results || [])
          .map((s) => s.placePrediction)
          .filter((p): p is PlacePrediction => Boolean(p));
        setSuggestions(preds);
        setOpen(preds.length > 0);
      } catch {
        if (!cancelled) {
          setSuggestions([]);
          setOpen(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, placesLibrary]);

  const handleSelect = async (pred: PlacePrediction) => {
    setOpen(false);
    setSuggestions([]);
    try {
      const place = pred.toPlace();
      await place.fetchFields({ fields: ["formattedAddress", "location"] });
      const address = place.formattedAddress || pred.text.text || "";
      if (inputRef.current) inputRef.current.value = address;
      setQuery("");
      onAddressTextChange?.(address);
      if (place.location) {
        onAddressSelect(address, place.location.lat(), place.location.lng());
      }
    } finally {
      // A session ends when a place is selected; start a fresh one next time.
      sessionTokenRef.current = null;
    }
  };

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        defaultValue={defaultValue}
        placeholder={apiIsLoaded ? placeholder : "Cargando..."}
        className={className}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          onAddressTextChange?.(e.target.value);
        }}
        onBlur={(e) => {
          // Delay so a suggestion click registers before the list closes.
          window.setTimeout(() => setOpen(false), 150);
          onAddressTextChange?.(e.target.value);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-gray-600 dark:bg-gray-800"
          role="listbox"
        >
          {suggestions.map((pred) => (
            <li key={pred.placeId} role="option" aria-selected={false}>
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                // onMouseDown (not onClick) so it fires before the input blur.
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(pred);
                }}
              >
                {pred.text.text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
